import { useId } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material'
import PropTypes from 'prop-types'
import { setToken } from 'actions/account'
import { initiateEmailChange } from 'api/user'
import http from 'common/http'
import ModalHeader, {
  ModalHeaderIdContext,
} from 'components/modal/modal-header'
import { TEST_IDS } from 'test-ids'

// Step-up re-authentication: a silently-refreshed token never carries a
// fresh auth_time (refresh-token exchanges don't re-enter credentials), so
// getAccessTokenSilently alone can never satisfy the API's step-up check.
// loginWithPopup with prompt=login forces a real re-authentication without
// navigating away from this dialog; a full redirect is the fallback when the
// popup is blocked or closed, which does lose the dialog's state.
const forceReauthentication = async (auth0) => {
  const { loginWithPopup, loginWithRedirect, getAccessTokenSilently } = auth0
  try {
    await loginWithPopup({
      authorizationParams: { prompt: 'login', max_age: 0 },
    })
  } catch {
    await loginWithRedirect({
      authorizationParams: { prompt: 'login', max_age: 0 },
    })
    return null
  }
  return getAccessTokenSilently()
}

export default function ChangeEmailDialog({
  open,
  onClose,
  onChangeInitiated,
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const auth0 = useAuth0()
  const titleId = useId()
  const [newEmail, setNewEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setNewEmail('')
    setConfirmEmail('')
    setError('')
    setSubmitting(false)
  }

  const handleClose = () => {
    if (submitting) return
    reset()
    onClose()
  }

  const errorMessageFor = (err) => {
    const status = err?.response?.status
    const serverMessage = err?.response?.data?.error || ''
    if (status === 403) return t('email_change_step_up_required')
    if (status === 409) return t('email_change_in_use')
    if (status === 429) return t('email_change_rate_limited')
    if (status === 400 && /different/i.test(serverMessage)) {
      return t('email_change_same_as_current')
    }
    return t('email_change_generic_error')
  }

  const handleSubmit = async () => {
    setError('')
    const trimmedNew = newEmail.trim().toLowerCase()
    const trimmedConfirm = confirmEmail.trim().toLowerCase()

    if (!trimmedNew || !trimmedConfirm) return
    if (trimmedNew !== trimmedConfirm) {
      setError(t('email_change_mismatch'))
      return
    }

    setSubmitting(true)
    try {
      const freshToken = await forceReauthentication(auth0)
      if (!freshToken) {
        // Redirected away for a full re-login; this component will unmount.
        return
      }
      http.withTokens(freshToken)
      dispatch(setToken(freshToken))

      await dispatch(initiateEmailChange(trimmedNew))
      reset()
      onChangeInitiated?.()
      onClose()
    } catch (err) {
      setError(errorMessageFor(err))
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      data-testid={TEST_IDS.changeEmailDialog}
    >
      <DialogContent sx={{ width: '40ch' }}>
        <ModalHeaderIdContext.Provider value={titleId}>
          <ModalHeader>{t('change_email')}</ModalHeader>
        </ModalHeaderIdContext.Provider>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          autoFocus
          margin="normal"
          type="email"
          label={t('new_email')}
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          disabled={submitting}
          data-testid={TEST_IDS.changeEmailNewEmailInput}
        />
        <TextField
          fullWidth
          margin="normal"
          type="email"
          label={t('confirm_new_email')}
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          disabled={submitting}
          data-testid={TEST_IDS.changeEmailConfirmInput}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={submitting}
          data-testid={TEST_IDS.changeEmailCancelButton}
        >
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !newEmail || !confirmEmail}
          data-testid={TEST_IDS.changeEmailSubmitButton}
        >
          {submitting ? (
            <CircularProgress size={20} />
          ) : (
            t('email_change_submit')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ChangeEmailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onChangeInitiated: PropTypes.func,
}
